import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { NzModalRef, NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { LucideAngularModule, LockKeyhole, UserRound, ShieldCheck, LogOut, LogIn } from 'lucide-angular';

export interface ReloginDialogData {
  username: string;
  submit: (password: string) => Promise<void>;
}

/** Kết quả đóng hộp thoại: true = đã đăng nhập lại, false = người dùng chọn đăng xuất. */
@Component({
  selector: 'jhi-relogin-dialog',
  standalone: true,
  imports: [FormsModule, NzButtonModule, NzInputModule, LucideAngularModule],
  template: `
    <header class="relogin-header">
      <span class="relogin-badge"><lucide-icon [img]="icons.LockKeyhole" [size]="26" aria-hidden="true" /></span>
      <div>
        <h2>Phiên đăng nhập đã hết hạn</h2>
        <p>Đăng nhập lại để tiếp tục công việc</p>
      </div>
    </header>
    <p class="relogin-message">
      Vui lòng nhập lại mật khẩu của tài khoản đang sử dụng.
    </p>
    <div class="relogin-notice">
      <lucide-icon [img]="icons.ShieldCheck" [size]="18" aria-hidden="true" />
      <span>Dữ liệu bạn đang nhập vẫn được giữ nguyên.</span>
    </div>
    <form (ngSubmit)="onSubmit()">
      <label class="relogin-label" for="relogin-username">Tên đăng nhập</label>
      <nz-input-group class="relogin-input relogin-account" [nzPrefix]="usernameIcon">
        <input nz-input id="relogin-username" name="username" [value]="data.username" disabled />
      </nz-input-group>
      <ng-template #usernameIcon><lucide-icon [img]="icons.UserRound" [size]="18" aria-hidden="true" /></ng-template>

      <label class="relogin-label" for="relogin-password">Mật khẩu</label>
      <nz-input-group class="relogin-input" [nzPrefix]="passwordIcon" [class.relogin-input-error]="errorMessage">
        <input
          nz-input
          id="relogin-password"
          name="password"
          type="password"
          autocomplete="current-password"
          placeholder="Nhập mật khẩu của bạn"
          [(ngModel)]="password"
          [disabled]="submitting"
          autofocus
        />
      </nz-input-group>
      <ng-template #passwordIcon><lucide-icon [img]="icons.LockKeyhole" [size]="18" aria-hidden="true" /></ng-template>

      @if (errorMessage) {
        <div class="relogin-error" role="alert">{{ errorMessage }}</div>
      }

      <div class="relogin-actions">
        <button nz-button class="relogin-logout" type="button" nzType="text" (click)="onLogout()" [disabled]="submitting">
          <lucide-icon [img]="icons.LogOut" [size]="16" aria-hidden="true" />
          Đăng xuất
        </button>
        <button nz-button class="relogin-submit" type="submit" nzType="primary" [nzLoading]="submitting" [disabled]="!password || submitting">
          @if (!submitting) { <lucide-icon [img]="icons.LogIn" [size]="17" aria-hidden="true" /> }
          Đăng nhập
        </button>
      </div>
    </form>
  `,
  styles: [`
    :host { display: block; color: #28352b; }
    .relogin-header { display: flex; align-items: center; gap: 14px; margin-bottom: 20px; }
    .relogin-badge { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 56px; height: 56px; border-radius: 16px; background: #eef5e9; color: #457738; }
    .relogin-header h2 { margin: 0 0 4px; font-size: 19px; line-height: 1.4; font-weight: 600; color: #355d2b; }
    .relogin-header p { margin: 0; color: #748071; font-size: 13px; }
    .relogin-message { margin: 0 0 12px; color: #626c60; line-height: 1.6; }
    .relogin-notice { display: flex; align-items: center; gap: 9px; padding: 12px 14px; border: 1px solid #deead7; border-radius: 10px; background: #f4f8f0; color: #457738; font-size: 13px; line-height: 1.5; }
    lucide-icon { display: inline-flex; flex-shrink: 0; }
    .relogin-label { display: block; margin: 18px 0 7px; font-weight: 500; font-size: 13px; }
    .relogin-input { padding: 10px 12px; border-radius: 8px; border-color: #dce3d8; color: #7c8b75; transition: border-color .2s, box-shadow .2s; }
    .relogin-input input { color: #28352b; }
    .relogin-input input:focus { box-shadow: none; }
    .relogin-input:hover, .relogin-input:focus-within { border-color: #457738; }
    .relogin-input:focus-within { box-shadow: 0 0 0 3px rgb(69 119 56 / 12%); }
    .relogin-account, .relogin-account:hover { background: #f5f7f3; border-color: #e2e7de; }
    .relogin-account input:disabled { background: transparent; color: #7b8577; }
    .relogin-input-error { border-color: #ff4d4f; }
    .relogin-error { margin-top: 12px; color: #ff4d4f; }
    .relogin-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 24px; padding-top: 20px; border-top: 1px solid #edf0e9; }
    .relogin-actions button { display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 40px; padding: 0 16px; border-radius: 8px; font-weight: 500; }
    .relogin-logout { color: #697464; }
    .relogin-logout:hover:enabled { color: #457738; background: #f4f8f0; }
    .relogin-submit { background: #457738; border-color: #457738; box-shadow: 0 2px 4px rgb(69 119 56 / 14%); }
    .relogin-submit:hover:enabled, .relogin-submit:focus-visible:enabled { background: #355d2b; border-color: #355d2b; }
    .relogin-submit:disabled { background: #e8eee3; border-color: #e0e7da; color: #8c9b83; box-shadow: none; }
    @media (max-width: 420px) {
      .relogin-header { gap: 10px; }
      .relogin-header h2 { font-size: 17px; }
      .relogin-badge { width: 46px; height: 46px; border-radius: 12px; }
    }
  `]
})
export class ReloginDialogComponent {
  readonly icons = { LockKeyhole, UserRound, ShieldCheck, LogOut, LogIn };
  readonly data = inject<ReloginDialogData>(NZ_MODAL_DATA);
  private modalRef = inject(NzModalRef);

  password = '';
  submitting = false;
  errorMessage = '';

  async onSubmit(): Promise<void> {
    if (!this.password || this.submitting) {
      return;
    }

    this.submitting = true;
    this.errorMessage = '';
    try {
      await this.data.submit(this.password);
      this.modalRef.close(true);
    } catch (error) {
      this.errorMessage = error instanceof HttpErrorResponse && error.status === 401
        ? 'Mật khẩu không đúng.'
        : error instanceof Error && error.message
          ? error.message
          : 'Không đăng nhập được, vui lòng thử lại.';
    } finally {
      this.submitting = false;
    }
  }

  onLogout(): void {
    this.modalRef.close(false);
  }
}
